import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import JSZip from "jszip";
import {
  Grid,
  Button,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import {
  getOrderStatus,
  getPaymentMethodByNumber,
  getTranscationTypeNameByNumber,
} from "../../../utils";
import "./index.css";
import moment from "moment";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";

const DownloadInvoice = () => {
  const agGridRef = useRef<AgGridReact | null>(null);
  const [msisdn, setmsisdn] = useState("");
  const [action, setAction] = useState(false);
  const [product, setProduct] = useState<number>(0);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [selectedInvoices, setSelectedInvoices] = useState<any[]>([]);
  const [actionDownload, setActionDownload] = useState(false);

  const handleGetInovices = async () => {
    setInvoices([]);
    setSelectedInvoices([]);
    toast.dismiss();
    if (msisdn.trim().length > 9 && product !== 0) {
      setAction(true);
      requestInvoices();
    } else {
      toast.error("Please enter valid inputs.");
    }
  };

  const requestInvoices = async () => {
    await axiosInterceptorInstance
      .get(
        `/dashboard/DownloadInvoicesData?Msisdn=${msisdn.trim()}&Product=${product}`,
      )
      .then((res: any) => {
        const data = JSON.parse(res.data.body);
        console.log(data);
        if (data == null) {
          toast.error("MSISDN not found.");
        } else {
          if (data.Data.length > 0) {
            data.Data.map((item: any) => {
              item.BundleName = item.BundleName == "" ? "--" : item.BundleName;
              item.TransactionType = getTranscationTypeNameByNumber(
                item.TransactionType,
              );
              item.PaymentMethodType = getPaymentMethodByNumber(
                item.PaymentMethodType,
              );
              item.OrderStatus = getOrderStatus(item.OrderStatus);
              item.TransactionDate = moment(item.TransactionDate).format(
                "DD-MM-YYYY",
              );
            });
            setInvoices(data.Data);
          } else {
            toast.info("Record Not found");
          }
        }
      })
      .catch((error: any) => {
        toast.error(error.message);
      })
      .finally(() => {
        setAction(false);
      });
  };

  const getInvoiceBlob = async (data: any) => {
    const isRWG = product === 6;
    const baseUrl = isRWG
      ? process.env.REACT_APP_RWG_API_URL
      : product === 1
        ? process.env.REACT_APP_THM_API_URL
        : process.env.REACT_APP_Now_API_URL;

    const url = isRWG
      ? `${baseUrl}v1/rwg/invoice`
      : `${baseUrl}v1/payment/generateinvoice`;

    const response = await fetch(
      isRWG
        ? `${url}?OrderId=${data.OrderID}&TransactionId=${data.TransactionId}`
        : url,
      {
        method: isRWG ? "GET" : "POST",
        headers: {
          "content-type": "application/json",
          accept: "application/octet-stream",
        },
        ...(!isRWG && { body: JSON.stringify({ orderId: data.OrderID }) }),
      },
    );

    if (response.status !== 200) {
      throw new Error("Unable to download invoice");
    }

    return response.blob();
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.target = "_blank";
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const getInvoiceFileName = (data: any, index?: number) => {
    return `Invoice-${data.OrderID}.pdf`;
  };

  const downloadInvoice = async (data: any) => {
    setActionDownload(true);

    try {
      const blob = await getInvoiceBlob(data);
      downloadBlob(blob, getInvoiceFileName(data));
    } catch (error) {
      console.error("Error:", error);
      toast.dismiss();
      toast.error("Unable to download invoice");
    } finally {
      setActionDownload(false);
    }
  };

  const handleSelectionChanged = () => {
    const selectedRows = agGridRef.current?.api.getSelectedRows() ?? [];
    setSelectedInvoices(selectedRows);
  };

  const downloadSelectedInvoices = async () => {
    const selectedRows = agGridRef.current?.api.getSelectedRows() ?? [];

    if (selectedRows.length === 0) {
      toast.error("Please select at least one invoice.");
      return;
    }

    setActionDownload(true);
    toast.dismiss();

    const zip = new JSZip();
    const failedOrderIds: string[] = [];

    for (let index = 0; index < selectedRows.length; index += 1) {
      const invoice = selectedRows[index];

      try {
        const blob = await getInvoiceBlob(invoice);
        zip.file(getInvoiceFileName(invoice, index), blob);
      } catch (error) {
        console.error("Error:", error);
        failedOrderIds.push(invoice.OrderID);
      }
    }

    try {
      if (failedOrderIds.length === selectedRows.length) {
        toast.error("Unable to download selected invoices.");
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      downloadBlob(zipBlob, "Invoices.zip");

      if (failedOrderIds.length > 0) {
        toast.warning(
          `Downloaded ${selectedRows.length - failedOrderIds.length} invoice(s). Failed OrderID(s): ${failedOrderIds.join(", ")}`,
        );
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Unable to create invoices ZIP.");
    } finally {
      setActionDownload(false);
    }
  };

  const ActionsCellRenderer: React.FC<{ data: any }> = ({ data }) => {
    return (
      <Button
        onClick={() => {
          downloadInvoice(data);
        }}
        className="dButton dButton2"
        variant="contained"
        style={{ height: "32px", textTransform: "none" }}
      >
        Download
      </Button>
    );
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      pinned: "left",
      filter: false,
      resizable: false,
      sortable: false,
    },
    {
      field: "TransactionDate",
      headerName: "Order Date",
      filter: true,
      resizable: true,
    },
    { field: "OrderID", headerName: "OrderID", filter: true, resizable: true },
    {
      field: "BundleName",
      headerName: "Bundle Name",
      filter: true,
      resizable: true,
    },
    {
      field: "TotalAmount",
      headerName: "Total Amount",
      filter: true,
      resizable: true,
    },
    {
      field: "TransactionId",
      headerName: "Transaction Id",
      filter: true,
      resizable: true,
    },
    {
      field: "TransactionType",
      headerName: "Transaction Type",
      filter: true,
      resizable: true,
    },
    {
      field: "PaymentMethodType",
      headerName: "Payment Type",
      filter: true,
      resizable: true,
    },
    {
      field: "OrderStatus",
      headerName: "Order Status",
      filter: true,
      resizable: true,
    },
    { headerName: "Invoice", cellRenderer: ActionsCellRenderer },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        {actionDownload && (
          <div className="dimBG">
            <CircularProgress size={40} />
          </div>
        )}
        <h2 className="sectionTitle">Download Invoices</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={4.5}>
              <TextField
                className="formControl"
                label="MSISDN"
                variant="outlined"
                defaultValue={msisdn}
                onChange={(e) => setmsisdn(e.target.value)}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid item xs={4.5}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel id="demo-simple-select-label">Product</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  label="anbu"
                  className="selectControl"
                  autoWidth
                  value={product}
                  defaultValue={product}
                  onChange={(e: any) => setProduct(parseInt(e.target.value))}
                >
                  <MenuItem value="0">Choose Product</MenuItem>
                  <MenuItem value="1">THM</MenuItem>
                  <MenuItem value="2">NOW</MenuItem>
                  <MenuItem value="6">RWG</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              {!action && (
                <Button
                  onClick={handleGetInovices}
                  className="dButton dButton2"
                  variant="contained"
                >
                  Search
                </Button>
              )}
              {action && (
                <Button className="dButton dButton2" variant="contained">
                  <CircularProgress color="inherit" size={30} />
                </Button>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
      {invoices.length > 0 && (
        <>
          <div className="invoiceToolbar">
            <Button
              onClick={downloadSelectedInvoices}
              className="dButton dButton2"
              variant="contained"
              disabled={actionDownload || selectedInvoices.length === 0}
            >
              Download Selected ({selectedInvoices.length})
            </Button>
          </div>
          <div
            className="ag-theme-alpine"
            style={{ width: "100%", height: " 600px", overflowY: "auto" }}
          >
            <AgGridReact
              ref={agGridRef}
              paginationPageSize={50}
              pagination={true}
              rowData={invoices}
              columnDefs={columnDefs}
              enableCellTextSelection={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onSelectionChanged={handleSelectionChanged}
            ></AgGridReact>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default DownloadInvoice;
